import { triggerBlobDownload } from "@/common/helpers/file";
import { showApiErrorToast } from "@/common/helpers/show-api-error-toast";
import { CommandButton } from "@/components/command-palette/command-button";
import { tokenizeJson } from "@/components/dat-snapshot/_helpers/dat-snapshot-json";
import {
  buildSnapshotQuickJumpDestinations,
  type SnapshotQuickJumpDestination,
} from "@/components/dat-snapshot/_helpers/dat-snapshot-quick-jump";
import { DatSnapshotJson, type SnapshotQuickJumpRequest } from "@/components/dat-snapshot/dat-snapshot-json";
import { DatSnapshotQuickJump } from "@/components/dat-snapshot/dat-snapshot-quick-jump";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { AlertTriangle, DownloadIcon, FileJson2Icon, SearchIcon } from "lucide-react";
import { type KeyboardEvent, useDeferredValue, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export interface SnapshotDownload {
  blob: Blob;
  fileName: string;
}

export interface DatSnapshotTranslations {
  description: string;
  downloadError: string;
  downloaded: string;
  downloadLabel: string;
  loadErrorDescription: string;
  loadErrorTitle: string;
  retryLoad: string;
  title: string;
  unavailableDescription: string;
  unavailableTitle: string;
  viewLabel: string;
}

interface DatSnapshotActionProps {
  canReadSnapshot: boolean;
  className?: string;
  showInCommandPalette?: boolean;
  size?: ButtonSize;
  snapshotUuid: string;
  translations: DatSnapshotTranslations;
  useSnapshotDownloadMutation: () => UseMutationResult<SnapshotDownload, Error, string, unknown>;
  useSnapshotQuery: (
    uuid: string,
    options?: { enabled?: boolean },
  ) => UseQueryResult<Record<string, unknown>, AxiosError>;
}

interface DatSnapshotDialogProps extends Omit<
  DatSnapshotActionProps,
  "canReadSnapshot" | "className" | "showInCommandPalette" | "size"
> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DatSnapshotDialog({
  open,
  onOpenChange,
  snapshotUuid,
  translations,
  useSnapshotDownloadMutation,
  useSnapshotQuery,
}: DatSnapshotDialogProps) {
  const { t } = useTranslation(["common", "fab"]);
  const [search, setSearch] = useState("");
  const [quickJumpRequest, setQuickJumpRequest] = useState<SnapshotQuickJumpRequest>();
  const quickJumpRequestIdRef = useRef(0);
  const deferredSearch = useDeferredValue(search);
  const snapshotQuery = useSnapshotQuery(snapshotUuid, { enabled: open });
  const downloadMutation = useSnapshotDownloadMutation();
  const hasSnapshot = snapshotQuery.data !== undefined;
  const formattedSnapshot = useMemo(
    () => (hasSnapshot ? (JSON.stringify(snapshotQuery.data, null, 2) ?? "") : ""),
    [hasSnapshot, snapshotQuery.data],
  );
  const snapshotTokens = useMemo(() => tokenizeJson(formattedSnapshot), [formattedSnapshot]);
  const quickJumpDestinations = useMemo(
    () =>
      buildSnapshotQuickJumpDestinations(
        formattedSnapshot,
        snapshotTokens.filter((token) => token.kind === "property"),
      ),
    [formattedSnapshot, snapshotTokens],
  );
  const isUnavailable = snapshotQuery.error?.response?.status === 404;

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      setSearch("");
      setQuickJumpRequest(undefined);
      quickJumpRequestIdRef.current = 0;
    }

    onOpenChange(nextOpen);
  };

  const handleQuickJump = (destination: SnapshotQuickJumpDestination): void => {
    quickJumpRequestIdRef.current += 1;
    setQuickJumpRequest({ id: quickJumpRequestIdRef.current, start: destination.start });
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape" && search) {
      setSearch("");
      event.stopPropagation();
    }
  };

  const handleDownload = async (): Promise<void> => {
    try {
      const download = await downloadMutation.mutateAsync(snapshotUuid);

      triggerBlobDownload(download.blob, download.fileName);
      toast.success(translations.downloaded);
    } catch (error) {
      showApiErrorToast(translations.downloadError, error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="grid h-[min(90vh,56rem)] max-h-[min(90vh,56rem)] grid-rows-[auto_auto_1fr] overflow-hidden select-none md:max-w-6xl"
        onClose={() => handleOpenChange(false)}
      >
        <DialogHeader className="pr-10">
          <DialogTitle>{translations.title}</DialogTitle>
          <DialogDescription>{translations.description}</DialogDescription>
        </DialogHeader>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="relative min-w-48 flex-1">
            <SearchIcon
              aria-hidden="true"
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2"
            />
            <Input
              aria-label={t("fab:SearchDatSnapshot")}
              className="pl-9 select-text"
              placeholder={t("fab:SearchDatSnapshot")}
              value={search}
              disabled={!hasSnapshot}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <DatSnapshotQuickJump
            destinations={quickJumpDestinations}
            disabled={!hasSnapshot}
            onSelect={handleQuickJump}
          />
          <Button
            type="button"
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            disabled={!hasSnapshot || downloadMutation.isPending}
            onClick={() => void handleDownload()}
          >
            {downloadMutation.isPending ? (
              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
            ) : (
              <DownloadIcon />
            )}
            {translations.downloadLabel}
          </Button>
        </div>

        {snapshotQuery.isPending ? (
          <div className="text-muted-foreground flex min-h-0 items-center justify-center gap-2 rounded-lg border">
            <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
            <span>{t("common:Loading")}</span>
          </div>
        ) : null}

        {snapshotQuery.isError ? (
          <div className="flex min-h-0 items-center justify-center">
            <Alert variant={isUnavailable ? "warning" : "error"} className="max-w-xl">
              <AlertTriangle />
              <AlertTitle>{isUnavailable ? translations.unavailableTitle : translations.loadErrorTitle}</AlertTitle>
              <AlertDescription>
                <p>{isUnavailable ? translations.unavailableDescription : translations.loadErrorDescription}</p>
                {!isUnavailable ? (
                  <Button
                    type="button"
                    size={ButtonSize.Sm}
                    variant={ButtonVariant.Outlined}
                    colorVariant={ButtonColorVariant.Secondary}
                    onClick={() => void snapshotQuery.refetch()}
                  >
                    {translations.retryLoad}
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          </div>
        ) : null}

        {hasSnapshot ? (
          <DatSnapshotJson
            quickJumpRequest={quickJumpRequest}
            search={deferredSearch}
            tokens={snapshotTokens}
            value={formattedSnapshot}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function DatSnapshotAction({
  canReadSnapshot,
  className,
  showInCommandPalette = false,
  size = ButtonSize.Default,
  snapshotUuid,
  translations,
  useSnapshotDownloadMutation,
  useSnapshotQuery,
}: DatSnapshotActionProps) {
  const [open, setOpen] = useState(false);

  if (!canReadSnapshot) {
    return null;
  }

  return (
    <>
      <CommandButton
        commandId={showInCommandPalette ? `dat-snapshot-${snapshotUuid}` : undefined}
        label={translations.viewLabel}
        icon={<FileJson2Icon />}
        className={className}
        size={size}
        variant={ButtonVariant.Outlined}
        colorVariant={ButtonColorVariant.Secondary}
        onClick={() => setOpen(true)}
      />
      <DatSnapshotDialog
        open={open}
        snapshotUuid={snapshotUuid}
        translations={translations}
        useSnapshotDownloadMutation={useSnapshotDownloadMutation}
        useSnapshotQuery={useSnapshotQuery}
        onOpenChange={setOpen}
      />
    </>
  );
}
